import React from "react";
import { queriesPayeeRows, queriesPayees } from "@query";
import { WSElement, WSText } from "@wingspanhq/fe-component-library";
import { selectorCounterpartyDisplayName } from "../../../../shared/selectors/selectorCounterpartyDisplayName";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { useXWingspanPayerId } from "../../../Payees/routes/RoutePayeeDetails/useXWingspanPayerId";

/**
 * Names the contractor a bulk-review row belongs to.
 *
 * `payeeId` is the canonical `Payee.id`, which is also what `searchPayeeRows`
 * publishes as its row id. The row is preferred because it carries
 * `profile.resolvedName`, the same string the contractor list shows; the `Payee`
 * read is the fallback for when no row comes back, and the caller prefetches it
 * so the fallback costs no extra request.
 */
export const PayeeName: React.FC<{ payeeId: string }> = ({ payeeId }) => {
  const xWingspanAccount = useXWingspanPayerId();
  const queryPayeeRow = queriesPayeeRows.searchPayeeRows.useQuery({
    filter: { id: { eq: payeeId } },
    page: { size: 1 },
    xWingspanAccount
  });
  const queryPayee = queriesPayees.getPayee.useQuery({
    payeeId,
    xWingspanAccount
  });

  const payeeRow = queryPayeeRow.data?.data?.[0];
  const name = payeeRow
    ? selectorSearchRowName(payeeRow)
    : queryPayee.data
      ? selectorCounterpartyDisplayName(queryPayee.data)
      : "";

  if (name) {
    return <>{name}</>;
  }

  // Both reads settled without a name — shimmering on past that would never
  // stop.
  if (!queryPayeeRow.isLoading && !queryPayee.isLoading) {
    return queryPayee.isError ? (
      <WSText.ParagraphXs color="red500">error</WSText.ParagraphXs>
    ) : (
      <>—</>
    );
  }

  return <WSElement as="i" shimmer pb="L" style={{ display: "block" }} />;
};
